// Adding and removing focus blocks from the web. Each is a gateway approval the executor runs; the
// web never writes to Google Calendar. Undo of a new block cancels its work while it still waits,
// else removes the block (one more approval, bound to exactly that event).
import type { QueryClient } from "@tanstack/react-query";
import type { FocusRequest, FocusResult } from "@shared/agenda";
import { dayApi, dayKeys } from "@web/lib/dayApi";
import { feedApi } from "@web/lib/feedApi";
import { feedKeys, invalidateBoards } from "@web/lib/feedQueries";
import { registerUndo } from "@web/lib/undoRegistry";
import { undoReversible } from "@web/lib/undoReversible";

/** Long enough to notice the toast and change one's mind; the block stays removable after. */
export const FOCUS_UNDO_MS = 10_000;

function refresh(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: dayKeys.agenda });
  void queryClient.invalidateQueries({ queryKey: ["work"] });
}

/** A card lined up behind its block (U3) leaves the round: refresh the board and the card. */
function refreshCard(queryClient: QueryClient, request: FocusRequest) {
  if (request.target.kind !== "card") return;
  invalidateBoards(queryClient);
  void queryClient.invalidateQueries({ queryKey: feedKeys.card(request.target.id), exact: true });
}

/**
 * Approves the block, then offers Undo. `label` names the time ("Today · 2:00–2:30 PM"). With
 * `lineUp` (a big card, U3) the card waits under In progress; Undo also brings it back to review.
 */
export async function addFocusBlock(
  queryClient: QueryClient,
  request: FocusRequest,
  label: string,
): Promise<FocusResult> {
  const result = await dayApi.addFocus(request);
  refresh(queryClient);
  const linedUp = !!result.linedUp && request.target.kind === "card";
  if (linedUp) refreshCard(queryClient, request);
  registerUndo({
    message: linedUp
      ? `Focus time added · ${label}. The card waits under In progress.`
      : `Adding focus time · ${label}`,
    durationMs: FOCUS_UNDO_MS,
    undo: async () => {
      const how = await undoReversible({
        cancel: () => feedApi.workAction(result.work.id, "cancel"),
        reverse: () => dayApi.removeFocus({ approvalId: result.approvalId }),
      });
      if (linedUp) {
        await feedApi.restore(request.target.id).catch(() => undefined);
        refreshCard(queryClient, request);
      }
      refresh(queryClient);
      return how;
    },
    doneMessage: (how) =>
      how === "cancelled"
        ? "Not added. Nothing reached your calendar."
        : "Taking it off your calendar.",
    tooLateMessage: "It’s still being added. You can remove it from Your day in a moment.",
  });
  return result;
}

/** Approves removing one block MeeMail made (the rail arms first; this is the confirm). */
export async function removeFocusBlock(queryClient: QueryClient, holdId: string): Promise<void> {
  await dayApi.removeFocus({ holdId });
  refresh(queryClient);
}
